import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Banknote, Building2, CheckCircle2, Landmark, RefreshCw, ShieldAlert, Wallet,
} from 'lucide-react';
import { fmtVES } from '@/components/LedgerStatementTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SEV_STYLE = {
  P0: 'bg-red-50 border-red-200 text-red-800',
  P1: 'bg-amber-50 border-amber-200 text-amber-800',
};

function Stat({ label, value, hint, tone = 'default', testid }) {
  const tones = {
    default: 'text-gray-900',
    good: 'text-emerald-700',
    warn: 'text-amber-700',
    bad: 'text-red-700',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${tones[tone]}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-gray-400">{hint}</p> : null}
    </div>
  );
}

export default function CongregationsAuditTab({ setToast }) {
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState('');
  const [report, setReport] = useState(null);
  const [withdrawals, setWithdrawals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkBank, setCheckBank] = useState(true);

  useEffect(() => {
    axios.get(`${API}/congregations/merchants`, { headers: headers() })
      .then(({ data }) => {
        setMerchants(data.merchants || []);
        if (data.merchants?.length) setMerchantId(data.merchants[0].user_id);
      })
      .catch(() => setToast?.({ type: 'error', message: 'No se pudieron cargar los comercios' }));
  }, [setToast]);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/congregations/audit?merchant_id=${merchantId}&check_bank=${checkBank}`,
        { headers: headers() },
      );
      setReport(data);
      const { data: wd } = await axios.get(
        `${API}/congregations/withdrawals?merchant_id=${merchantId}&limit=50`,
        { headers: headers() },
      );
      setWithdrawals(wd);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error en la auditoría' });
    } finally {
      setLoading(false);
    }
  }, [merchantId, checkBank, setToast]);

  useEffect(() => { load(); }, [load]);

  const retryCredits = async () => {
    try {
      const { data } = await axios.post(`${API}/congregations/pending-credits/retry`, {}, { headers: headers() });
      setToast?.({ type: 'success', message: `${data.count} crédito(s) reprocesado(s)` });
      load();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'No se pudo reintentar' });
    }
  };

  const verifyWithdrawals = async () => {
    try {
      const { data } = await axios.post(`${API}/congregations/withdrawals/verify-now`, {}, { headers: headers() });
      setToast?.({ type: 'success', message: `${data.checked} retiro(s) verificados contra el banco` });
      load();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'No se pudo verificar' });
    }
  };

  const t = report?.totals || {};
  const findings = report?.findings || [];

  return (
    <div className="space-y-5" data-testid="congregations-audit-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Comercio agregador</label>
          <select
            data-testid="congregations-merchant-select"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm min-w-[280px]"
          >
            {merchants.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.company_name || m.email} ({m.congregations_count})
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox" checked={checkBank} data-testid="congregations-check-bank"
            onChange={(e) => setCheckBank(e.target.checked)}
          />
          Cruzar contra saldo del banco
        </label>
        <button
          data-testid="congregations-refresh-btn" onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Auditando…' : 'Auditar'}
        </button>
      </div>

      {report ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              testid="congregations-total-balance" label="Saldo congregaciones"
              value={fmtVES(t.congregations_balance)}
              hint={`${t.congregations} congregaciones · ${t.active_projects} con actividad`}
            />
            <Stat
              testid="congregations-commission-balance" label="Comisiones Caura"
              value={fmtVES(t.commission_balance)} hint="acumuladas sin liquidar"
            />
            <Stat
              testid="congregations-bank-available" label="Disponible en banco"
              value={t.bank_checked ? fmtVES(t.bank_available) : 'No verificado'}
              hint={t.bank_checked ? `Cuenta ${t.bank_account}` : t.bank_reason}
              tone={t.bank_checked ? 'default' : 'warn'}
            />
            <Stat
              testid="congregations-bank-diff" label="Interno − banco"
              value={t.bank_diff === null || t.bank_diff === undefined ? '—' : fmtVES(t.bank_diff)}
              hint={`Fees banco estimados (no descontados): ${fmtVES(t.estimated_bank_fees)}`}
              tone={t.bank_diff > 1000 ? 'bad' : t.bank_diff === null ? 'default' : 'good'}
            />
          </div>

          {findings.length ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="congregations-findings">
              <div className="mb-2 flex items-center gap-2 font-semibold text-red-800">
                <ShieldAlert className="h-4 w-4" />
                {findings.length} hallazgo(s) de integridad
              </div>
              <ul className="space-y-1.5">
                {findings.map((f, i) => (
                  <li
                    key={`${f.type}-${f.project_id || i}`}
                    className={`rounded-lg border px-3 py-2 text-sm ${SEV_STYLE[f.severity] || SEV_STYLE.P1}`}
                    data-testid={`congregations-finding-${i}`}
                  >
                    <span className="font-semibold">[{f.severity}] {f.type}</span>
                    {f.project_name ? <span className="ml-1 font-medium">· {f.project_name}</span> : null}
                    <span className="ml-1">— {f.detail}</span>
                  </li>
                ))}
              </ul>
              {report.pending_credits?.length ? (
                <button
                  data-testid="congregations-retry-credits-btn" onClick={retryCredits}
                  className="mt-3 rounded-lg bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800"
                >
                  Reintentar {report.pending_credits.length} crédito(s) pendientes
                </button>
              ) : null}
            </div>
          ) : (
            <div
              className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              data-testid="congregations-no-findings"
            >
              <CheckCircle2 className="h-4 w-4" />
              Sin descuadres: cada saldo coincide con sus movimientos y todas las órdenes pagadas están acreditadas.
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
              <Building2 className="h-4 w-4" />
              Congregaciones con actividad
              <span className="ml-auto text-xs font-normal text-gray-400">
                {report.idle_projects} sin actividad (ocultas)
              </span>
            </div>
            <table className="w-full text-sm" data-testid="congregations-table">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Congregación</th>
                  <th className="px-4 py-2 text-left">Código</th>
                  <th className="px-4 py-2 text-right">Saldo</th>
                  <th className="px-4 py-2 text-right">Recalculado</th>
                  <th className="px-4 py-2 text-right">Dif.</th>
                  <th className="px-4 py-2 text-right">Órdenes</th>
                  <th className="px-4 py-2 text-right">Bruto cobrado</th>
                  <th className="px-4 py-2 text-right">Movs.</th>
                </tr>
              </thead>
              <tbody>
                {(report.projects || []).map((p) => (
                  <tr
                    key={p.project_id} className="border-t border-gray-100"
                    data-testid={`congregation-row-${p.code || p.project_id}`}
                  >
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5 text-gray-400" />{p.name}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{p.code || '—'}</td>
                    <td className="px-4 py-2 text-right font-medium">{fmtVES(p.balance)}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{fmtVES(p.computed_balance)}</td>
                    <td className={`px-4 py-2 text-right ${Math.abs(p.drift) > 0.01 ? 'font-semibold text-red-700' : 'text-gray-400'}`}>
                      {fmtVES(p.drift)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {p.orders_paid}
                      {p.uncredited_orders ? (
                        <span className="ml-1 inline-flex items-center gap-1 text-xs text-red-700">
                          <AlertTriangle className="h-3 w-3" />{p.uncredited_orders}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">{fmtVES(p.gross_collected)}</td>
                    <td className="px-4 py-2 text-right text-gray-400">{p.movements}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="flex items-center gap-2 text-xs text-gray-400">
            <Landmark className="h-3.5 w-3.5" />
            Los fees bancarios (1,5% P2P interbancario / 2% débito inmediato) no se descuentan de los
            saldos por decisión de negocio: se muestran como estimado para explicar la diferencia
            esperada contra el banco.
          </p>

          {withdrawals?.withdrawals?.length ? (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="congregations-withdrawals">
              <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
                <Banknote className="h-4 w-4" />
                Retiros de congregaciones
                <span className="text-xs font-normal text-gray-400">
                  {withdrawals.pending_count} en espera del banco · {fmtVES(withdrawals.pending_total)}
                </span>
                <button
                  onClick={verifyWithdrawals} data-testid="congregations-verify-withdrawals-btn"
                  className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Verificar contra el banco
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Congregación</th>
                    <th className="px-4 py-2 text-right">Monto</th>
                    <th className="px-4 py-2 text-left">Cuenta</th>
                    <th className="px-4 py-2 text-left">Estado</th>
                    <th className="px-4 py-2 text-left">Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.withdrawals.map((w) => (
                    <tr key={w.withdrawal_id} className="border-t border-gray-100" data-testid={`admin-withdrawal-${w.withdrawal_id}`}>
                      <td className="px-4 py-2 text-gray-500">
                        {w.created_at ? new Date(w.created_at).toLocaleString('es-VE') : '—'}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{w.congregation_code || '—'}</td>
                      <td className="px-4 py-2 text-right font-medium">{fmtVES(w.amount)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">···{w.destination_account}</td>
                      <td className="px-4 py-2 text-xs">{w.status}{w.verification_status ? ` · ${w.verification_status}` : ''}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">{w.bank_reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
